import axios, { AxiosError } from "axios";
import AuthClient from "./AuthClient";
import {
  ApiResponse,
  handleAxiosError,
} from "./responses/AxiosResponseHandler";

interface MissingClubsQueryParameters {
  City: string;
  PartyDate: Date | string;
  BookingType: number;
}

export interface MissingClubApiItem {
  ClubId: string;
  PictureUrl: string | null;
  DisplayName: string;  
}

export interface MissingClubsApiResponse {
  missingClubs: MissingClubApiItem[];
}

class MissingClubsForPromoterGroupApi {
  private authClient: AuthClient;

  constructor(authClient: AuthClient) {
    this.authClient = authClient;
  }

  private parseMissingClub(item: MissingClubApiItem | string): MissingClubApiItem {
    if (typeof item !== "string") {
      return item;
    }

    const [ClubId, PictureUrl, DisplayName ] = item
      .split("|")
      .map((value) => value.replace(/"/g, ""));

    return {
      ClubId,
      PictureUrl: PictureUrl ?? null,
      DisplayName
    };
  }

  private mapBookingTypeToRequestValue(bookingType: string | number): number {
    const normalizedBookingType = bookingType.toString().toLowerCase();

    if (normalizedBookingType === "guestlist" || normalizedBookingType === "0" || normalizedBookingType === "1") {
      return 1;
    }

    if (
      normalizedBookingType === "tablebooking" ||
      normalizedBookingType === "table" ||
      normalizedBookingType === "2"
    ) {
      return 2;
    }

    return 1;
  }

  public async getMissingClubs(
    city: string,
    partyDate: Date | string,
    bookingType: string | number
  ): Promise<ApiResponse<MissingClubsApiResponse> | undefined> {
    try {
      const authenticationResponse = this.authClient.getAuthenticationResponse();
      if (authenticationResponse == null) {
        return undefined;
      }

      if (!city || !partyDate) {
        return {
          data: {
            missingClubs: [],
          },
          statusCode: 400,
          errorMessage: "City and party date are required.",
        };
      }

      const queryParams: MissingClubsQueryParameters = {
        City: city,
        PartyDate: partyDate,
        BookingType: this.mapBookingTypeToRequestValue(bookingType),
      };

      const response = await axios.get(
        `${this.authClient.getApiRoot()}/Clubs/MissingClubs`,
        {
          params: queryParams,
          headers: {
            Authorization: `Bearer ${authenticationResponse.token}`,
          },
        }
      );

      return {
        statusCode: response.status,
        errorMessage: "",
        data: {
          missingClubs: ((response.data ?? []) as Array<MissingClubApiItem | string>).map(
            (item) => this.parseMissingClub(item)
          ),
        },
      };
    } catch (error) {
      const errorResponse = handleAxiosError<MissingClubsApiResponse>(
        error as AxiosError
      );
      return {
        statusCode: errorResponse.statusCode,
        errorMessage: errorResponse.errorMessage,
        data: errorResponse.data,
      };
    }
  }
}

export default MissingClubsForPromoterGroupApi;
